/**
 * Kiosk UUID registration system
 * Kiosks must be activated with an activation code
 */

const STORAGE_KEY = 'kiosk_uuid';
const STORAGE_INFO_KEY = 'kiosk_info';

let cachedKioskId: string | null = null;

/**
 * Get the kiosk UUID from localStorage
 * Returns null if not registered
 */
export function getKioskId(): string | null {
  if (cachedKioskId) {
    return cachedKioskId;
  }

  try {
    const storedId = localStorage.getItem(STORAGE_KEY);
    if (storedId && storedId.trim() !== '') {
      cachedKioskId = storedId;
      return storedId;
    }
  } catch (error) {
    console.warn('Failed to get kiosk UUID from localStorage:', error);
  }

  return null;
}

/**
 * Set the kiosk UUID after activation
 */
export function setKioskId(uuid: string, name: string, location?: string): boolean {
  try {
    if (!uuid || uuid.trim() === '') {
      return false;
    }

    localStorage.setItem(STORAGE_KEY, uuid.trim());
    localStorage.setItem(STORAGE_INFO_KEY, JSON.stringify({ name, location }));
    cachedKioskId = uuid.trim();
    return true;
  } catch (error) {
    console.error('Failed to set kiosk UUID:', error);
    return false;
  }
}

/**
 * Reset kiosk registration
 */
export function resetKioskId(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_INFO_KEY);
    cachedKioskId = null;
  } catch (error) {
    console.error('Failed to reset kiosk registration:', error);
  }
}

/**
 * Get current kiosk info
 */
export function getKioskIdInfo(): { kioskId: string | null; name?: string; location?: string; isRegistered: boolean } {
  const kioskId = getKioskId();
  const isRegistered = kioskId !== null;

  let name, location;
  try {
    const infoStr = localStorage.getItem(STORAGE_INFO_KEY);
    if (infoStr) {
      const info = JSON.parse(infoStr);
      name = info.name;
      location = info.location;
    }
  } catch (error) {
    console.warn('Failed to get kiosk info:', error);
  }

  return {
    kioskId,
    name,
    location,
    isRegistered
  };
}

/**
 * Check if kiosk is registered
 */
export function isKioskRegistered(): boolean {
  return getKioskId() !== null;
}